import { useMemo, useRef, useState } from 'react';
import type { Page } from '@connaro/shared';
import { api } from '../api/client';
import { OverlapsPanel } from '../components/OverlapsPanel';
import { EmptyState, ErrorState, Loading } from '../components/states';
import { useAsync } from '../hooks/useAsync';
import { useT } from '../i18n/I18nProvider';

interface ViewNode {
  deviceId: string;
  siteId: string;
  siteName: string;
  role: string;
  regionId: string | null;
  siteGroupId: string | null;
  latitude: number | null;
  longitude: number | null;
  x: number;
  y: number;
  reachability: string;
  routing: { enabled: boolean; routerId: string | null; protocols: string[] };
  tunnels: number;
  warnings: string[];
}

interface ViewLink {
  a: string;
  b: string;
  kind: string;
  state: 'active' | 'standby' | 'down' | 'planned';
  relayed: boolean;
  lastHandshakeAt: string | null;
  handshakeAgeSeconds: number | null;
  rxBytes: string;
  txBytes: string;
  allowedIps: string[];
  warnings: string[];
}

interface TopologyView {
  fabricId: string;
  topology: string;
  nodes: ViewNode[];
  links: ViewLink[];
  summary: Record<string, number>;
  warnings: string[];
}

interface Fabric {
  id: string;
  name: string;
  type: string;
}

type Selection = { kind: 'node'; id: string } | { kind: 'link'; id: string } | null;

const VIEWBOX = 1000;

function formatBytes(value: string): string {
  const n = Number(value);
  if (!Number.isFinite(n)) return value;
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let size = n;
  let unit = 0;
  while (size >= 1024 && unit < units.length - 1) {
    size /= 1024;
    unit += 1;
  }
  return `${unit === 0 ? size : size.toFixed(1)} ${units[unit]}`;
}

function formatAge(seconds: number | null, never: string): string {
  if (seconds === null) return never;
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`;
  return `${Math.floor(seconds / 86400)}d`;
}

const linkId = (l: ViewLink): string => `${l.a}|${l.b}`;

/**
 * Project a node into the SVG viewbox.
 *
 * The logical view uses the server's stable ring layout; the geographic view
 * uses an equirectangular projection of the site's coordinates. Sites without
 * coordinates are simply absent from the map rather than being placed at
 * (0, 0), which would put them in the Atlantic and read as real data.
 */
function project(node: ViewNode, mode: 'logical' | 'geographic'): { x: number; y: number } | null {
  if (mode === 'logical') return { x: node.x * VIEWBOX, y: node.y * VIEWBOX };
  if (node.latitude === null || node.longitude === null) return null;
  return {
    x: ((node.longitude + 180) / 360) * VIEWBOX,
    y: ((90 - node.latitude) / 180) * VIEWBOX,
  };
}

export function TopologyPage(): JSX.Element {
  const t = useT();
  const [fabricId, setFabricId] = useState('');
  const [mode, setMode] = useState<'logical' | 'geographic'>('logical');
  const [selection, setSelection] = useState<Selection>(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [showHubs, setShowHubs] = useState(true);
  const [showSpokes, setShowSpokes] = useState(true);
  const [relayedOnly, setRelayedOnly] = useState(false);
  const [problemsOnly, setProblemsOnly] = useState(false);
  const drag = useRef<{ x: number; y: number } | null>(null);

  // The fabrics list endpoint is paginated (the Page shape), so unwrap `.items`
  // to the array the rest of this view works with.
  const fabrics = useAsync(
    () => api.get<Page<Fabric>>('/fabrics?limit=200').then((r) => r.items),
    [],
  );
  const selected = fabricId || fabrics.data?.[0]?.id || '';
  const view = useAsync(
    () =>
      selected
        ? api.get<TopologyView>(`/sdwan/fabrics/${selected}/view`)
        : Promise.resolve(null as TopologyView | null),
    [selected],
  );

  const visible = useMemo(() => {
    const data = view.data;
    if (!data) return { nodes: [] as ViewNode[], links: [] as ViewLink[] };

    const nodes = data.nodes.filter((n) => {
      if (n.role === 'hub' && !showHubs) return false;
      if (n.role !== 'hub' && !showSpokes) return false;
      if (problemsOnly && n.warnings.length === 0) return false;
      return true;
    });
    const ids = new Set(nodes.map((n) => n.deviceId));

    const links = data.links.filter((l) => {
      // Keep visible.links meaningful on its own: a link whose endpoint was
      // filtered out is not visible. The renderer also skips it for want of a
      // position, so this is the semantic half of a belt-and-braces pair.
      if (!ids.has(l.a) || !ids.has(l.b)) return false;
      if (relayedOnly && !l.relayed) return false;
      if (problemsOnly && l.state === 'active' && l.warnings.length === 0) return false;
      return true;
    });
    return { nodes, links };
  }, [view.data, showHubs, showSpokes, relayedOnly, problemsOnly]);

  const positions = useMemo(() => {
    const map = new Map<string, { x: number; y: number }>();
    for (const node of visible.nodes) {
      const p = project(node, mode);
      if (p) map.set(node.deviceId, p);
    }
    return map;
  }, [visible.nodes, mode]);

  const detail = useMemo(() => {
    if (!view.data || !selection) return null;
    if (selection.kind === 'node') {
      return view.data.nodes.find((n) => n.deviceId === selection.id) ?? null;
    }
    return view.data.links.find((l) => linkId(l) === selection.id) ?? null;
  }, [view.data, selection]);

  const resetView = (): void => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  };

  if (fabrics.loading) return <Loading />;
  if (fabrics.error) return <ErrorState message={fabrics.error.message} onRetry={fabrics.reload} />;
  if (!fabrics.data || fabrics.data.length === 0) {
    return <EmptyState hint={t('topology.noFabrics')} />;
  }

  return (
    <section>
      <div className="page-head">
        <h1>{t('topology.title')}</h1>
        <div className="row-actions">
          <label htmlFor="fabric-select" className="sr-only">
            {t('topology.fabric')}
          </label>
          <select
            id="fabric-select"
            value={selected}
            onChange={(e) => {
              setFabricId(e.target.value);
              setSelection(null);
              resetView();
            }}
          >
            {fabrics.data.map((f) => (
              <option key={f.id} value={f.id}>
                {f.name}
              </option>
            ))}
          </select>
          <button
            type="button"
            className={mode === 'logical' ? 'btn btn--primary' : 'btn btn--ghost'}
            onClick={() => setMode('logical')}
          >
            {t('topology.logical')}
          </button>
          <button
            type="button"
            className={mode === 'geographic' ? 'btn btn--primary' : 'btn btn--ghost'}
            onClick={() => setMode('geographic')}
          >
            {t('topology.geographic')}
          </button>
        </div>
      </div>

      {view.loading ? <Loading /> : null}
      {view.error ? <ErrorState message={view.error.message} onRetry={view.reload} /> : null}

      {view.data ? (
        <>
          <dl className="detail-grid">
            <dt>{t('topology.sites')}</dt>
            <dd>{view.data.summary.sites}</dd>
            <dt>{t('topology.tunnels')}</dt>
            <dd>{view.data.summary.tunnels}</dd>
            <dt>{t('topology.active')}</dt>
            <dd>{view.data.summary.active}</dd>
            <dt>{t('topology.standby')}</dt>
            <dd>{view.data.summary.standby}</dd>
            <dt>{t('topology.planned')}</dt>
            <dd>{view.data.summary.planned}</dd>
            <dt>{t('topology.relayed')}</dt>
            <dd>{view.data.summary.relayed}</dd>
          </dl>

          {selected ? <OverlapsPanel fabricId={selected} /> : null}

          <fieldset className="form form--inline">
            <legend>{t('topology.filters')}</legend>
            <label>
              <input
                type="checkbox"
                checked={showHubs}
                onChange={(e) => setShowHubs(e.target.checked)}
              />
              {t('topology.showHubs')}
            </label>
            <label>
              <input
                type="checkbox"
                checked={showSpokes}
                onChange={(e) => setShowSpokes(e.target.checked)}
              />
              {t('topology.showSpokes')}
            </label>
            <label>
              <input
                type="checkbox"
                checked={relayedOnly}
                onChange={(e) => setRelayedOnly(e.target.checked)}
              />
              {t('topology.showRelayed')}
            </label>
            <label>
              <input
                type="checkbox"
                checked={problemsOnly}
                onChange={(e) => setProblemsOnly(e.target.checked)}
              />
              {t('topology.showProblems')}
            </label>
            <button
              type="button"
              className="btn btn--ghost"
              onClick={() => setZoom((z) => z * 1.25)}
            >
              {t('topology.zoomIn')}
            </button>
            <button
              type="button"
              className="btn btn--ghost"
              onClick={() => setZoom((z) => Math.max(0.25, z / 1.25))}
            >
              {t('topology.zoomOut')}
            </button>
            <button type="button" className="btn btn--ghost" onClick={resetView}>
              {t('topology.resetView')}
            </button>
          </fieldset>

          {view.data.nodes.length === 0 ? (
            <EmptyState hint={t('topology.empty')} />
          ) : mode === 'geographic' && positions.size === 0 ? (
            <EmptyState hint={t('topology.noGeo')} />
          ) : (
            <div className="table-wrap">
              <svg
                role="img"
                aria-label={t('topology.title')}
                viewBox={`0 0 ${VIEWBOX} ${VIEWBOX}`}
                className="topology-canvas"
                onMouseDown={(e) => {
                  drag.current = { x: e.clientX, y: e.clientY };
                }}
                onMouseUp={() => {
                  drag.current = null;
                }}
                onMouseLeave={() => {
                  drag.current = null;
                }}
                onMouseMove={(e) => {
                  if (!drag.current) return;
                  const dx = e.clientX - drag.current.x;
                  const dy = e.clientY - drag.current.y;
                  drag.current = { x: e.clientX, y: e.clientY };
                  setPan((p) => ({ x: p.x + dx, y: p.y + dy }));
                }}
              >
                <g transform={`translate(${pan.x} ${pan.y}) scale(${zoom})`}>
                  {visible.links.map((l) => {
                    const from = positions.get(l.a);
                    const to = positions.get(l.b);
                    if (!from || !to) return null;
                    const id = linkId(l);
                    return (
                      <line
                        key={id}
                        x1={from.x}
                        y1={from.y}
                        x2={to.x}
                        y2={to.y}
                        className={`topology-link topology-link--${l.state}${
                          l.relayed ? ' topology-link--relayed' : ''
                        }${selection?.kind === 'link' && selection.id === id ? ' is-selected' : ''}`}
                        onClick={() => setSelection({ kind: 'link', id })}
                      >
                        <title>{`${l.kind} · ${t(`topology.${l.state}`)}`}</title>
                      </line>
                    );
                  })}

                  {visible.nodes.map((n) => {
                    const p = positions.get(n.deviceId);
                    if (!p) return null;
                    const isSelected = selection?.kind === 'node' && selection.id === n.deviceId;
                    return (
                      <g
                        key={n.deviceId}
                        transform={`translate(${p.x} ${p.y})`}
                        className={`topology-node topology-node--${n.role}${
                          n.warnings.length > 0 ? ' topology-node--warn' : ''
                        }${isSelected ? ' is-selected' : ''}`}
                        onClick={() => setSelection({ kind: 'node', id: n.deviceId })}
                      >
                        <circle r={n.role === 'hub' ? 18 : 12} />
                        <text y={-24} textAnchor="middle">
                          {n.siteName}
                        </text>
                        <title>{`${n.siteName} · ${n.tunnels}`}</title>
                      </g>
                    );
                  })}
                </g>
              </svg>
            </div>
          )}

          <h2>{selection?.kind === 'link' ? t('topology.link') : t('topology.node')}</h2>
          {detail === null ? (
            <p>{t('topology.selectNode')}</p>
          ) : 'siteName' in detail ? (
            <dl className="detail-grid">
              <dt>{t('topology.node')}</dt>
              <dd>{detail.siteName}</dd>
              <dt>{t('topology.role')}</dt>
              <dd>{detail.role === 'hub' ? t('topology.hub') : t('topology.spoke')}</dd>
              <dt>{t('topology.tunnels')}</dt>
              <dd>{detail.tunnels}</dd>
              <dt>{t('topology.reachability')}</dt>
              <dd>{detail.reachability === 'public' ? t('topology.public') : t('topology.nat')}</dd>
              <dt>{t('topology.routing')}</dt>
              <dd>
                {detail.routing.enabled
                  ? `${detail.routing.routerId ?? '—'} · ${detail.routing.protocols.join(', ') || '—'}`
                  : t('topology.noRouting')}
              </dd>
              <dt>{t('topology.warnings')}</dt>
              <dd>
                {detail.warnings.length === 0 ? (
                  t('topology.noWarnings')
                ) : (
                  <ul>
                    {detail.warnings.map((w) => (
                      <li key={w}>{w}</li>
                    ))}
                  </ul>
                )}
              </dd>
            </dl>
          ) : (
            <dl className="detail-grid">
              <dt>{t('topology.linkKind')}</dt>
              <dd>{detail.kind}</dd>
              <dt>{t('topology.state')}</dt>
              <dd>{t(`topology.${detail.state}`)}</dd>
              <dt>{t('topology.relayed')}</dt>
              <dd>{detail.relayed ? '✓' : '—'}</dd>
              <dt>{t('topology.lastHandshake')}</dt>
              <dd>{formatAge(detail.handshakeAgeSeconds, t('topology.never'))}</dd>
              <dt>{t('topology.traffic')}</dt>
              <dd>{`↓ ${formatBytes(detail.rxBytes)} · ↑ ${formatBytes(detail.txBytes)}`}</dd>
              <dt>{t('topology.carries')}</dt>
              <dd>
                {detail.allowedIps.length === 0
                  ? '—'
                  : detail.allowedIps.map((a) => <code key={a}> {a} </code>)}
              </dd>
              <dt>{t('topology.warnings')}</dt>
              <dd>
                {detail.warnings.length === 0 ? (
                  t('topology.noWarnings')
                ) : (
                  <ul>
                    {detail.warnings.map((w) => (
                      <li key={w}>{w}</li>
                    ))}
                  </ul>
                )}
              </dd>
            </dl>
          )}

          {view.data.warnings.length > 0 ? (
            <div className="banner banner--warning">
              <ul>
                {view.data.warnings.map((w) => (
                  <li key={w}>{w}</li>
                ))}
              </ul>
            </div>
          ) : null}
        </>
      ) : null}
    </section>
  );
}
